import React, { useEffect, useState } from "react";
import Layout from "../layout/Layout";
import styles from "./Inbox.module.css"; // Create an Inbox.module.css for your styling
import { Connection, InboxItemType, SeenStatus, useListInboxInvitesQuery, useListPlaydateInvitesQuery, User } from "../../gql/types";
import { InboxConnection } from "./types";
import PlaydateInviteInboundView from "./playdate/PlaydateInboundView";
import PlaydateInviteOutboundView from "./playdate/PlaydateOutboundView";
import { useInboxUpdates } from "../../contexts/InboxUpdatesContext";
import { LOCAL_MODE } from "../../config/local";

enum TabType {
    OUTBOUND,
    INBOUND,
}

enum RequestType {
    PLAYDATE,
    CHILDCARE
}

export interface InboxProps {
    onCloseInbox: () => void;
    user?: User;
    // filter by a target user or family
    target?: string;
    targetConnection?: Connection;
    isHidden?: boolean;
    precomputedConnection?: InboxConnection;
    baseZIndex?: number;
}

const Inbox: React.FC<InboxProps> = ({
    onCloseInbox,
    user,
    target,
    targetConnection,
    isHidden,
    precomputedConnection,
    baseZIndex,
}) => {
    const [selectedTab, setSelectedTab] = useState<TabType>(TabType.INBOUND);
    const [selectedRequestType, setSelectedRequestType] = useState<RequestType>(
        RequestType.PLAYDATE
    );
    // Initialize state with precomputed data if available.
    const [inboundPlaydateData, setInboundPlaydateData] = useState(
        precomputedConnection?.Inbound?.Playdate || null
    );
    const [outboundPlaydateData, setOutboundPlaydateData] = useState(
        precomputedConnection?.Outbound?.Playdate || null
    );

    const [inboundNewCount, setInboundNewCount] = useState(0);
    const [outboundNewCount, setOutboundNewCount] = useState(0);

    const { optimisticUpdates } = useInboxUpdates();

    // Fetch the inbox invites once at the Inbox level.
    const {
        data: playdateInboundData,
        loading: playdateInboundLoading,
        fetchMore: playdateInboundFetchMore,
        refetch: playdateInboundRefetch,
    } = useListInboxInvitesQuery({
        variables: {
            guestID: user?.id || "",
            seenState: null,
            since: null,
            hasOrganizerID: target ?? null,
            limit: 10,
            cursor: null,
        },
        fetchPolicy: LOCAL_MODE ? "no-cache" : "cache-and-network",
        skip: !user || isHidden,
    });

    const onPlaydateInboundResponse = () => {
        playdateInboundRefetch();
    };

    const {
        data: playdateOutboundData,
        loading: playdateOutboundLoading,
        fetchMore: playdateOutboundFetchMore,
        refetch: playdateOutboundRefetch,
    } = useListPlaydateInvitesQuery({
        variables: {
            organizerID: user?.id || "",
            seenState: null,
            since: null,
            hasGuestID: target ?? null,
            limit: 10,
            cursor: null,
        },
        fetchPolicy: LOCAL_MODE ? "no-cache" : "cache-and-network",
        skip: !user || isHidden,
    });

    const onPlaydateOutboundResponse = () => {
        playdateOutboundRefetch();
    };


    // Update state when new data arrives.
    useEffect(() => {
        if (playdateInboundData?.listInboxInvites) {
            // You can merge with inboxData if needed or simply replace it:
            setInboundPlaydateData(playdateInboundData.listInboxInvites);
        }

        if (playdateOutboundData?.listPlaydateInvites) {
            setOutboundPlaydateData(playdateOutboundData?.listPlaydateInvites);
        }
    }, [playdateInboundData, playdateOutboundData]);

    // For inbound data:
    useEffect(() => {
        if (inboundPlaydateData && inboundPlaydateData.edges) {
            const inboundUpdates = optimisticUpdates.filter(
                (update) => update.itemType === InboxItemType.InboundPlaydate
            );
            let hasChanged = false;
            const updatedEdges = inboundPlaydateData.edges.map((edge) => {
                if (edge.node.guestUpdate) {
                    const override = inboundUpdates.find(
                        (u) => u.updateID === edge.node.guestUpdate?.updateID
                    );
                    if (override && edge.node.guestUpdate.seenState !== override.seenStatus) {
                        hasChanged = true;
                        return {
                            ...edge,
                            node: {
                                ...edge.node,
                                guestUpdate: {
                                    ...edge.node.guestUpdate,
                                    seenState: override.seenStatus,
                                },
                            },
                        };
                    }
                }
                return edge;
            });
            if (hasChanged) {
                setInboundPlaydateData({
                    ...inboundPlaydateData,
                    edges: updatedEdges,
                });
            }
        }
    }, [optimisticUpdates, inboundPlaydateData]);

    // For outbound data:
    useEffect(() => {
        if (outboundPlaydateData && outboundPlaydateData.edges) {
            const outboundUpdates = optimisticUpdates.filter(
                (update) => update.itemType === InboxItemType.OutboundPlaydate
            );
            let hasChanged = false;
            const updatedEdges = outboundPlaydateData.edges.map((edge) => {
                if (edge.node.organizerUpdate) {
                    const override = outboundUpdates.find(
                        (u) => u.updateID === edge.node.organizerUpdate?.updateID
                    );
                    if (override && edge.node.organizerUpdate.seenState !== override.seenStatus) {
                        hasChanged = true;
                        return {
                            ...edge,
                            node: {
                                ...edge.node,
                                organizerUpdate: {
                                    ...edge.node.organizerUpdate,
                                    seenState: override.seenStatus,
                                },
                            },
                        };
                    }
                }
                return edge;
            });

            if (hasChanged) {
                setOutboundPlaydateData({
                    ...outboundPlaydateData,
                    edges: updatedEdges,
                });
            }
        }
    }, [optimisticUpdates, outboundPlaydateData]);

    useEffect(() => {
        if (inboundPlaydateData && inboundPlaydateData.edges) {
            const count = inboundPlaydateData.edges.filter(
                (edge) =>
                    edge.node.guestUpdate &&
                    edge.node.guestUpdate.seenState === SeenStatus.New
            ).length;
            setInboundNewCount(count);
        } else {
            setInboundNewCount(0);
        }
    }, [inboundPlaydateData]);

    // Calculate NEW count for outbound invites.
    useEffect(() => {
        if (outboundPlaydateData && outboundPlaydateData.edges) {
            const count = outboundPlaydateData.edges.filter(
                (edge) =>
                    edge.node.organizerUpdate &&
                    edge.node.organizerUpdate.seenState === SeenStatus.New
            ).length;
            setOutboundNewCount(count);
        } else {
            setOutboundNewCount(0);
        }
    }, [outboundPlaydateData]);


    const getIsActive = (tab: TabType, req: RequestType) =>
        tab === selectedTab && req === selectedRequestType && !isHidden;

    return (
        <Layout
            navbarConfig={{
                title: "Requests",
                showLogo: false,
                hamburgerMenu: false,
                onBack: onCloseInbox,
            }}
            isHidden={isHidden}
            baseZIndex={baseZIndex ? baseZIndex + 1 : 1}
        >
            <div className={styles.inboxContainer}>
                <div className={styles.tabRow}>
                    <button
                        className={`${styles.tabButton} ${selectedTab === TabType.OUTBOUND ? styles.activeTab : ""
                            }`}
                        onClick={() => setSelectedTab(TabType.OUTBOUND)}
                    >
                        Outbound
                    </button>
                    <button
                        className={`${styles.tabButton} ${selectedTab === TabType.INBOUND ? styles.activeTab : ""
                            }`}
                        onClick={() => setSelectedTab(TabType.INBOUND)}
                    >
                        Inbound
                    </button>
                </div>
                <div className={styles.requestTypeContainer}>
                    <button
                        className={`${styles.requestTypeButton} ${selectedRequestType === RequestType.PLAYDATE
                            ? styles.activeRequestType
                            : ""
                            }`}
                        onClick={() => setSelectedRequestType(RequestType.PLAYDATE)}
                    >
                        Playdate{" "}
                        {selectedTab === TabType.INBOUND && inboundNewCount > 0 && (
                            <span className={styles.newCountBadge}>{inboundNewCount}</span>
                        )}
                        {selectedTab === TabType.OUTBOUND && outboundNewCount > 0 && (
                            <span className={styles.newCountBadge}>{outboundNewCount}</span>
                        )}
                    </button>
                    {/* Additional buttons for other request types */}
                </div>
                <div className={styles.contentDivider}></div>
                <div className={styles.requestsOuterContainer}>
                    <PlaydateInviteInboundView
                        user={user}
                        connectionData={inboundPlaydateData == null ? undefined : inboundPlaydateData}
                        targetConnection={targetConnection}
                        loading={playdateInboundLoading}
                        fetchMore={playdateInboundFetchMore}
                        onResponse={onPlaydateInboundResponse}
                        active={getIsActive(TabType.INBOUND, RequestType.PLAYDATE)}
                    />
                    <PlaydateInviteOutboundView
                        user={user}
                        connectionData={outboundPlaydateData == null ? undefined : outboundPlaydateData}
                        targetConnection={targetConnection}
                        loading={playdateOutboundLoading}
                        fetchMore={playdateOutboundFetchMore}
                        onResponse={onPlaydateOutboundResponse}
                        active={getIsActive(TabType.OUTBOUND, RequestType.PLAYDATE)}
                    />
                </div>
            </div>
        </Layout>
    );
};

export default Inbox;